/**
 * Cart Inventory Validation — Impulse 8.2.0
 * Supports standard products + Gift Ship bundle products (_gs_bundle_ids)
 * Cart page only
 *
 * - Standard OOS products: flagged + "Remove unavailable items" button
 * - Bundle with OOS child: flagged + "Edit your box" button → Gift Ship builder
 *
 * Install:
 *   1. Upload cart-validation.js to Themes > Assets
 *   2. Paste  {{ 'cart-validation.js' | asset_url | script_tag }}
 *      into sections/main-cart.liquid just above {% schema %}
 */

(function () {
  'use strict';

  if (!window.location.pathname.includes('/cart')) return;

  // ─── Selectors ────────────────────────────────────────────────────────────
  const SEL = {
    cartForm:       '#CartPageForm',
    itemRow:        '.cart__item',
    itemName:       '.cart__item-name',
    qtyInput:       '.js-qty__num',
    qtyAdjust:      '.js-qty__adjust',
    checkoutBtn:    'button[name="checkout"]',
    additionalBtns: '.additional-checkout-buttons',
  };

  // ─── Styles ───────────────────────────────────────────────────────────────
  const CSS = `
    #oos-banner {
      background: #fff8ed;
      border: 1px solid #f0a500;
      border-radius: 5px;
      padding: 14px 18px;
      margin-bottom: 24px;
      font-size: 14px;
      line-height: 1.55;
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }
    #oos-banner svg { flex-shrink: 0; margin-top: 2px; }
    #oos-banner-body { flex: 1; }
    #oos-banner-body strong { display: block; margin-bottom: 3px; font-size: 14px; }

    .oos-action-btn {
      display: inline-block;
      margin-top: 10px;
      margin-right: 8px;
      background: #222;
      color: #fff;
      border: none;
      border-radius: 3px;
      padding: 8px 16px;
      font-size: 13px;
      cursor: pointer;
      letter-spacing: 0.02em;
      text-decoration: none;
      vertical-align: middle;
    }
    .oos-action-btn:hover { background: #000; color: #fff; }
    .oos-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .oos-action-btn--secondary {
      background: transparent;
      color: #222;
      border: 1px solid #222;
    }
    .oos-action-btn--secondary:hover { background: #f5f5f5; color: #222; }

    .oos-item-row {
      outline: 2px solid #fca5a5;
      outline-offset: 3px;
      border-radius: 3px;
    }
    .oos-badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      background: #fee2e2;
      color: #b91c1c;
      border-radius: 3px;
      font-size: 11px;
      font-weight: 700;
      padding: 3px 8px;
      margin-top: 6px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .oos-badge svg { flex-shrink: 0; }
    .oos-bundle-detail {
      font-size: 12px;
      color: #b91c1c;
      margin-top: 5px;
      line-height: 1.5;
    }
    .oos-bundle-detail span {
      display: block;
    }
    .oos-bundle-detail__intro {
      margin-bottom: 3px;
      font-weight: 600;
    }
    .oos-bundle-detail__intro::before {
      content: none !important;
    }
    .oos-bundle-detail__instruction {
      margin-top: 6px;
      color: #6b4c4c;
      font-style: italic;
    }
    .oos-bundle-detail__instruction::before {
      content: none !important;
    }
    .oos-bundle-detail span:not(.oos-bundle-detail__intro):not(.oos-bundle-detail__instruction)::before {
      content: "· ";
    }
    .oos-bundle-edit-link {
      display: inline-block;
      margin-top: 8px;
      font-size: 12px;
      color: #222;
      text-decoration: underline;
      cursor: pointer;
    }
    .oos-bundle-edit-link:hover { color: #000; }
    .oos-qty-disabled {
      opacity: 0.35 !important;
      pointer-events: none !important;
    }
    .oos-checkout-note {
      display: block;
      text-align: center;
      font-size: 12px;
      color: #b91c1c;
      margin-top: 7px;
    }
    button[name="checkout"].oos-btn-disabled {
      opacity: 0.45 !important;
      pointer-events: none !important;
      cursor: not-allowed !important;
    }
  `;

  function injectStyles() {
    if (document.getElementById('oos-val-styles')) return;
    const s = document.createElement('style');
    s.id = 'oos-val-styles';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  // ─── Fetch cart ───────────────────────────────────────────────────────────
  async function fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  // ─── Check a single variant ───────────────────────────────────────────────
  async function isVariantAvailable(variantId) {
    try {
      const res = await fetch(`/variants/${variantId}.js`);
      if (!res.ok) return true;
      const data = await res.json();
      return data.available === true;
    } catch {
      return true; // fail open
    }
  }

  // ─── Extract Gift Ship builder URL from product description ───────────────
  // product_description contains: window.location.href='/a/gs/builder/1473/1';
  function extractBuilderUrl(description) {
    if (!description) return null;
    const match = description.match(/window\.location\.href='([^']+)'/);
    return match ? match[1] : null;
  }

  // ─── Resolve OOS status for a cart item ──────────────────────────────────
  async function resolveItemOOS(item) {
    const props = item.properties || {};
    const isBundle = item.has_components === true && props._gs_bundle_ids;

    if (!isBundle) {
      const available = await isVariantAvailable(item.variant_id);
      return { oos: !available, oosChildren: [], builderUrl: null };
    }

    // Bundle — check each child variant
    let bundleIds = props._gs_bundle_ids;
    let bundleNames = props._gs_bundle_contents || [];

    if (typeof bundleIds === 'string') {
      try { bundleIds = JSON.parse(bundleIds); } catch { bundleIds = [bundleIds]; }
    }
    if (typeof bundleNames === 'string') {
      try { bundleNames = JSON.parse(bundleNames); } catch { bundleNames = [bundleNames]; }
    }

    const childResults = await Promise.all(
      bundleIds.map(async (id, i) => {
        const available = await isVariantAvailable(id);
        if (available) return null;
        const raw = bundleNames[i] || `Item ${i + 1}`;
        const name = raw.split(' - Quantity:')[0].trim();
        return name;
      })
    );

    const oosChildren = childResults.filter(Boolean);
    const builderUrl = oosChildren.length > 0
      ? extractBuilderUrl(item.product_description)
      : null;

    return { oos: oosChildren.length > 0, oosChildren, builderUrl };
  }

  // ─── Flag a cart item row ─────────────────────────────────────────────────
  function flagRow(row, oosChildren, builderUrl, key) {
    if (row.querySelector('.oos-badge')) return;
    row.classList.add('oos-item-row');

    const nameEl = row.querySelector(SEL.itemName);
    const titleWrap = nameEl?.closest('.cart__item-title') || row.querySelector('.cart__item-title');

    const badge = document.createElement('div');
    badge.className = 'oos-badge';
    badge.innerHTML = `
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="3">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      ${oosChildren.length > 0 ? 'Bundle item unavailable' : 'Out of Stock'}
    `;

    if (nameEl) {
      nameEl.insertAdjacentElement('afterend', badge);
    } else if (titleWrap) {
      titleWrap.appendChild(badge);
    }

    // List OOS child names for bundles
    if (oosChildren.length > 0) {
      const detail = document.createElement('div');
      detail.className = 'oos-bundle-detail';

      // Intro line
      const intro = document.createElement('span');
      intro.className = 'oos-bundle-detail__intro';
      const itemWord = oosChildren.length === 1 ? 'item has' : 'items have';
      intro.textContent = `The following ${itemWord} sold out since you built this box:`;
      detail.appendChild(intro);

      // Per-item lines
      oosChildren.forEach(name => {
        const span = document.createElement('span');
        span.textContent = name;
        detail.appendChild(span);
      });

      // Instruction line
      const instruction = document.createElement('span');
      instruction.className = 'oos-bundle-detail__instruction';
      instruction.textContent = `On the next page, please remove ${oosChildren.length === 1 ? 'it' : 'them'} from your box and choose a replacement item, then add your updated box back to cart.`;
      detail.appendChild(instruction);

      badge.insertAdjacentElement('afterend', detail);

      // "Edit your box" inline button under the detail
      if (builderUrl) {
        const editLink = document.createElement('button');
        editLink.className = 'oos-bundle-edit-link';
        editLink.type = 'button';
        editLink.textContent = 'Edit your box →';
        editLink.dataset.bundleKey = key || '';
        editLink.dataset.builderUrl = builderUrl;
        editLink.addEventListener('click', () => removeAndRedirect(editLink));
        detail.insertAdjacentElement('afterend', editLink);
      }
    }

    // Disable qty controls
    row.querySelectorAll(`${SEL.qtyInput}, ${SEL.qtyAdjust}`).forEach(el => {
      el.classList.add('oos-qty-disabled');
      el.setAttribute('disabled', 'true');
    });
  }

  // ─── Disable checkout ─────────────────────────────────────────────────────
  function disableCheckout(count) {
    const btn = document.querySelector(SEL.checkoutBtn);
    if (!btn) return;

    btn.setAttribute('disabled', 'disabled');
    btn.classList.add('oos-btn-disabled');

    const extra = document.querySelector(SEL.additionalBtns);
    if (extra) extra.style.display = 'none';

    if (!document.querySelector('.oos-checkout-note')) {
      const note = document.createElement('span');
      note.className = 'oos-checkout-note';
      const word = count === 1 ? 'item' : 'items';
      note.textContent = `Please resolve the ${count} unavailable ${word} above to continue to checkout`;
      btn.insertAdjacentElement('afterend', note);
    }
  }

  // ─── Top banner ───────────────────────────────────────────────────────────
  // Handles mixed scenario: some standard OOS + some broken bundles
  function injectBanner(standardOosKeys, brokenBundles) {
    if (document.getElementById('oos-banner')) return;

    const totalCount = standardOosKeys.length + brokenBundles.length;
    const s = totalCount === 1;

    // Build action buttons
    let actionsHtml = '';

    if (standardOosKeys.length > 0) {
      actionsHtml += `<button id="oos-remove-btn" class="oos-action-btn" type="button">
        Remove unavailable ${standardOosKeys.length === 1 ? 'item' : 'items'}
      </button>`;
    }

    if (brokenBundles.length > 0 && brokenBundles[0].builderUrl) {
      actionsHtml += `<button
        id="oos-edit-box-btn"
        type="button"
        class="oos-action-btn ${standardOosKeys.length > 0 ? 'oos-action-btn--secondary' : ''}"
        data-bundle-key="${brokenBundles[0].key}"
        data-builder-url="${brokenBundles[0].builderUrl}">
        Edit your box
      </button>`;
    }

    const banner = document.createElement('div');
    banner.id = 'oos-banner';
    banner.setAttribute('role', 'alert');
    banner.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
           stroke="#b45309" stroke-width="2" aria-hidden="true">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0
                 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      <div id="oos-banner-body">
        <strong>
          ${totalCount} ${s ? 'item' : 'items'} in your cart
          ${s ? 'requires' : 'require'} your attention.
        </strong>
        ${brokenBundles.length > 0
          ? (() => {
              const allOosNames = brokenBundles.flatMap(b => b.oosChildren);
              const nameList = allOosNames.map(n => `<strong>${n}</strong>`).join(', ');
              const verb = allOosNames.length === 1 ? 'has' : 'have';
              const it = allOosNames.length === 1 ? 'it' : 'them';
              return `${nameList} ${verb} sold out since you built your box. On the next page, please remove ${it} from your box and choose a replacement item — then add your updated box back to cart.`;
            })()
          : `${s ? 'This item has' : 'These items have'} gone out of stock since you last visited. Please remove ${s ? 'it' : 'them'} to continue to checkout.`
        }
        <div>${actionsHtml}</div>
      </div>
    `;

    const form = document.querySelector(SEL.cartForm);
    if (form) {
      form.insertAdjacentElement('beforebegin', banner);
    }

    // Wire remove button for standard OOS items
    const removeBtn = document.getElementById('oos-remove-btn');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => removeItems(standardOosKeys));
    }

    // Wire Edit your box button in banner
    const editBoxBtn = document.getElementById('oos-edit-box-btn');
    if (editBoxBtn) {
      editBoxBtn.addEventListener('click', () => removeAndRedirect(editBoxBtn));
    }
  }

  // ─── Remove standard OOS items then reload ───────────────────────────────
  async function removeItems(keys) {
    const btn = document.getElementById('oos-remove-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Removing…'; }

    const updates = {};
    keys.forEach(k => { updates[k] = 0; });

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      window.location.reload();
    } catch (err) {
      console.error('[Cart Validation] Remove failed:', err);
      if (btn) { btn.disabled = false; btn.textContent = 'Try again'; }
    }
  }

  // ─── Remove broken bundle then redirect to Gift Ship builder ──────────────
  async function removeAndRedirect(triggerEl) {
    triggerEl.disabled = true;
    triggerEl.textContent = 'Removing…';

    const key = triggerEl.dataset.bundleKey;
    const url = triggerEl.dataset.builderUrl;

    if (!key || !url) {
      window.location.href = url || '/';
      return;
    }

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [key]: 0 } }),
      });
    } catch (err) {
      console.error('[Cart Validation] Bundle remove failed:', err);
    }

    // Redirect regardless — even if the remove call fails, send them to the builder
    window.location.href = url;
  }

  // ─── Main ─────────────────────────────────────────────────────────────────
  async function validateCart() {
    let cart;
    try {
      cart = await fetchCart();
    } catch {
      return;
    }

    if (!cart.items || cart.items.length === 0) return;

    const results = await Promise.all(
      cart.items.map(async item => {
        const { oos, oosChildren, builderUrl } = await resolveItemOOS(item);
        return { key: item.key, oos, oosChildren, builderUrl };
      })
    );

    const oosResults = results.filter(r => r.oos);
    if (oosResults.length === 0) return;

    injectStyles();

    // Split into standard OOS vs broken bundles
    const standardOos   = oosResults.filter(r => r.oosChildren.length === 0);
    const brokenBundles = oosResults.filter(r => r.oosChildren.length > 0);

    // Flag DOM rows
    oosResults.forEach(({ key, oosChildren, builderUrl }) => {
      const row = document.querySelector(`.cart__item[data-key="${key}"]`);
      if (row) flagRow(row, oosChildren, builderUrl, key);
    });

    injectBanner(
      standardOos.map(r => r.key),
      brokenBundles
    );

    disableCheckout(oosResults.length);
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', validateCart);
  } else {
    validateCart();
  }

})();